/**
 * Compliance footer (PR B) — TREC IABS/CPN links for REAL_ESTATE tenants, the
 * fixed SML §80.200(b) complaint notice for MORTGAGE tenants, and the
 * sponsoring-brokerage copyright line every public page needs.
 *
 * Shared by BrokeragePage (no `agent` prop — brokerage-only) and the
 * agent-landing page (PR C, `agent` set). A dual-licensed agent (Realtor +
 * MLO, detected server-side via the V043 phone-key cross-tenant lookup) gets
 * BOTH disclosure blocks plus the Texas Finance Code §157.024(a)(10)
 * advance-disclosure line; a single-licensed agent gets only their own side.
 *
 * The two `agent` states render DIFFERENT layouts (Checkpoint #16, user
 * feedback): BrokeragePage's own footer is UNTOUCHED (broker identity is the
 * page's whole point, logo already sits at the top of the page). The
 * agent-landing page instead bundles each side's full identity — logo,
 * license, address, phone, compliance links, and the agent's own
 * identification line for that side — into one BrokerCard, laid out side by
 * side for a dual-licensed agent. The SML notice (mortgage-side only) renders
 * ONCE below both cards rather than inside the mortgage card, since it's a
 * shared disclosure, not per-brokerage content.
 */

import { EqualHousingNotice } from "../../shell/EqualHousing";
import {
  resolveApiHref,
  type AgentPublic,
  type BrokeragePublic,
  type TenantLookupResponse,
} from "./passkeyApi";

const NMLS_CONSUMER_ACCESS_URL = "https://www.nmlsconsumeraccess.org/";

/** A legal name such as "Hilltop Lending Inc." already ends the sentence. */
function withFullStop(name: string): string {
  return name.endsWith(".") ? name : `${name}.`;
}

const SML_COMPLAINT_NOTICE = (
  <p>
    CONSUMERS WISHING TO FILE A COMPLAINT AGAINST A COMPANY OR A RESIDENTIAL
    MORTGAGE LOAN ORIGINATOR SHOULD COMPLETE AND SEND A COMPLAINT FORM TO THE
    TEXAS DEPARTMENT OF SAVINGS AND MORTGAGE LENDING, 2601 NORTH LAMAR
    BOULEVARD, AUSTIN, TEXAS 78705. A TOLL-FREE CONSUMER HOTLINE IS AVAILABLE AT
    (877) 276-5550. THE DEPARTMENT MAINTAINS A RECOVERY FUND TO MAKE PAYMENTS OF
    CERTAIN ACTUAL OUT OF POCKET DAMAGES SUSTAINED BY BORROWERS CAUSED BY ACTS
    OF LICENSED RESIDENTIAL MORTGAGE LOAN ORIGINATORS. A WRITTEN APPLICATION FOR
    REIMBURSEMENT FROM THE RECOVERY FUND MUST BE FILED WITH AND INVESTIGATED BY
    THE DEPARTMENT PRIOR TO THE PAYMENT OF A CLAIM. FOR MORE INFORMATION ABOUT
    THE RECOVERY FUND, PLEASE CONSULT THE DEPARTMENT'S WEBSITE AT
    WWW.SML.TEXAS.GOV.
  </p>
);

function MortgageBlock({
  agentName,
  agentNmlsId,
}: {
  agentName?: string;
  agentNmlsId?: string | null;
}) {
  return (
    <div style={{ marginTop: "var(--rp-space-2)" }}>
      {SML_COMPLAINT_NOTICE}
      <div className="compliance-footer__links">
        <a href={NMLS_CONSUMER_ACCESS_URL} target="_blank" rel="noreferrer">
          NMLS Consumer Access
        </a>
      </div>
      {agentName && agentNmlsId && (
        <div>
          {agentName}, Mortgage Loan Originator, NMLS #{agentNmlsId}.
        </div>
      )}
    </div>
  );
}

function RealEstateBlock({
  iabsUrl,
  cpnUrl,
  agentName,
  agentTrecId,
}: {
  iabsUrl: string | null | undefined;
  cpnUrl: string;
  agentName?: string;
  agentTrecId?: string | null;
}) {
  return (
    <div>
      <div className="compliance-footer__links">
        {iabsUrl && (
          <a href={resolveApiHref(iabsUrl)} target="_blank" rel="noreferrer">
            Information About Brokerage Services (IABS)
          </a>
        )}
        {cpnUrl && (
          <a href={resolveApiHref(cpnUrl)} target="_blank" rel="noreferrer">
            Consumer Protection Notice
          </a>
        )}
      </div>
      {agentName && agentTrecId && (
        <div>
          {agentName}, Texas REALTOR®, TREC #{agentTrecId}.
        </div>
      )}
    </div>
  );
}

/** One side's full identity for the agent-landing page — logo, license,
 * address, phone, that side's compliance links, and the agent's own
 * identification line — bundled into a single card. Laid out side by side
 * with the other side for a dual-licensed agent (BrokerRow). */
function BrokerCard({
  brokerage,
  brokerageType,
  agentName,
  agentLicenseNumber,
  iabsUrl,
}: {
  brokerage: BrokeragePublic;
  brokerageType: string;
  agentName?: string;
  agentLicenseNumber?: string | null;
  iabsUrl?: string | null;
}) {
  const isMortgage = brokerageType === "MORTGAGE";
  const cityLine = [
    brokerage.address_city,
    brokerage.address_state,
    brokerage.address_zip,
  ]
    .filter(Boolean)
    .join(", ");
  const license = brokerage.trec_license_number
    ? `TREC #${brokerage.trec_license_number}`
    : brokerage.nmls_id
      ? `NMLS #${brokerage.nmls_id}`
      : null;
  return (
    <div className="compliance-footer__broker-card">
      {brokerage.logo_url && (
        <img
          src={brokerage.logo_url}
          alt=""
          aria-hidden
          className="compliance-footer__broker-logo"
        />
      )}
      <div>
        <span className="compliance-footer__broker-name">
          {brokerage.legal_name}
        </span>
        {license && <div>{license}</div>}
        {brokerage.address_street && <div>{brokerage.address_street}</div>}
        {cityLine && <div>{cityLine}</div>}
        {brokerage.primary_phone && <div>{brokerage.primary_phone}</div>}
        <div className="compliance-footer__links">
          {!isMortgage && iabsUrl && (
            <a href={resolveApiHref(iabsUrl)} target="_blank" rel="noreferrer">
              Information About Brokerage Services (IABS)
            </a>
          )}
          {!isMortgage && brokerage.cpn_url && (
            <a
              href={resolveApiHref(brokerage.cpn_url)}
              target="_blank"
              rel="noreferrer"
            >
              Consumer Protection Notice
            </a>
          )}
          {isMortgage && (
            <a href={NMLS_CONSUMER_ACCESS_URL} target="_blank" rel="noreferrer">
              NMLS Consumer Access
            </a>
          )}
        </div>
        {agentName && agentLicenseNumber && (
          <div>
            {agentName},{" "}
            {isMortgage ? "Mortgage Loan Originator" : "Texas REALTOR®"},{" "}
            {isMortgage ? "NMLS" : "TREC"} #{agentLicenseNumber}.
          </div>
        )}
      </div>
    </div>
  );
}

export function ComplianceFooter({
  tenant,
  agent,
}: {
  tenant: TenantLookupResponse;
  /** Set on the agent-landing page (PR C); omitted on the brokerage-only page. */
  agent?: AgentPublic;
}) {
  const b = tenant.brokerage;
  if (!b) return null;

  const isMortgage = tenant.brokerage_type === "MORTGAGE";
  const year = new Date().getFullYear();
  const dual = agent?.dual_licensed ?? false;

  // Which title the copyright line uses — a dual agent is both; a
  // single-licensed one is whichever side this tenant is.
  const title = dual
    ? "Texas REALTOR® / Mortgage Loan Originator"
    : isMortgage
      ? "Mortgage Loan Originator"
      : "Texas REALTOR®";

  // BrokeragePage (no agent) — UNCHANGED from the original design; the whole
  // point of that page IS the broker, whose logo already sits at its top.
  if (!agent) {
    const cityLine = [b.address_city, b.address_state, b.address_zip]
      .filter(Boolean)
      .join(", ");
    const license = b.trec_license_number
      ? `TREC #${b.trec_license_number}`
      : b.nmls_id
        ? `NMLS #${b.nmls_id}`
        : null;
    return (
      <footer className="compliance-footer">
        <span className="compliance-footer__broker-name">{b.legal_name}</span>
        {license && <div>{license}</div>}
        {b.address_street && <div>{b.address_street}</div>}
        {cityLine && <div>{cityLine}</div>}
        {b.primary_phone && <div>{b.primary_phone}</div>}

        {!isMortgage && (
          <RealEstateBlock iabsUrl={b.iabs_url} cpnUrl={b.cpn_url} />
        )}
        {isMortgage && <MortgageBlock />}

        <div style={{ marginTop: "var(--rp-space-2)" }}>
          <EqualHousingNotice className="compliance-footer__eho" />
        </div>
        <div style={{ marginTop: "var(--rp-space-2)" }}>
          © {year} {withFullStop(b.legal_name)}
        </div>
      </footer>
    );
  }

  // Agent-landing page — one BrokerCard per side, side by side for a dual
  // agent; the SML notice (if either side is MORTGAGE) renders once below
  // both, since it's a shared disclosure, not per-brokerage content.
  const secondaryIsMortgage = agent.secondary_brokerage_type === "MORTGAGE";
  const showSmlNotice = isMortgage || (dual && secondaryIsMortgage);

  return (
    <footer className="compliance-footer">
      <div className="compliance-footer__broker-row">
        <BrokerCard
          brokerage={b}
          brokerageType={tenant.brokerage_type}
          agentName={agent.full_name}
          agentLicenseNumber={
            isMortgage ? agent.nmls_id : agent.trec_license_number
          }
          iabsUrl={agent.iabs_url ?? b.iabs_url}
        />
        {dual && agent.secondary_brokerage && (
          <BrokerCard
            brokerage={agent.secondary_brokerage}
            brokerageType={agent.secondary_brokerage_type ?? ""}
            agentName={agent.full_name}
            agentLicenseNumber={agent.secondary_license_number}
            iabsUrl={agent.secondary_brokerage.iabs_url}
          />
        )}
      </div>

      <div className="compliance-footer__broker-divider" />

      {showSmlNotice && (
        <div style={{ marginTop: "var(--rp-space-2)" }}>
          {SML_COMPLAINT_NOTICE}
        </div>
      )}

      {dual && (
        <div style={{ marginTop: "var(--rp-space-2)" }}>
          <p>
            <strong>ADVANCE DISCLOSURE OF DUAL CAPACITY</strong> (Texas Finance
            Code §157.024(a)(10)): {agent.full_name} is acting as both a
            licensed real estate agent and a licensed mortgage loan originator
            in connection with this transaction, representing{" "}
            {isMortgage ? agent.secondary_brokerage_name : b.legal_name} on the
            real estate side and{" "}
            {isMortgage ? b.legal_name : agent.secondary_brokerage_name} on the
            mortgage side. You are not required to use {agent.full_name} or
            either brokerage for your financing or representation — you may
            choose any licensed provider for either service.
          </p>
        </div>
      )}

      <div style={{ marginTop: "var(--rp-space-2)" }}>
        <EqualHousingNotice className="compliance-footer__eho" />
      </div>
      <div style={{ marginTop: "var(--rp-space-2)" }}>
        © {year} {agent.full_name}, {title}. Sponsoring Brokerage:{" "}
        {withFullStop(
          dual && agent.secondary_brokerage_name
            ? `${b.legal_name} & ${agent.secondary_brokerage_name}`
            : b.legal_name,
        )}
      </div>
    </footer>
  );
}
